// PoReadOnlyPane — the purchase order beside an invoice, read only, so a
// bookkeeper checks the bill against the order without leaving the review.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import ItemName from '../components/ItemName';
import { formatEventDate } from '../utils/date-utils';
import {
  usePoApi, PoDetail, PoLine, PO_STATUS_LABELS, PO_LINE_TYPE_LABELS,
} from '../Procurement/poApi';
import { PO_STATUS_TAG } from '../Procurement/poTones';
import Tag from '../components/ui/Tag';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { formatCents } from './apInvoicesApi';
import { formatUnits } from './lineVerdict';

export interface PoReadOnlyPaneProps {
  purchaseOrderId: string;
  /** PO line ids this invoice bills. */
  highlightLineIds?: string[];
}

type PaneState =
  | { status: 'loading' }
  | { status: 'ready'; po: PoDetail }
  | { status: 'error' };

interface PaneMeta { highlighted: ReadonlySet<string> }

const netReceived = (l: PoLine) => l.quantity_received - (l.quantity_returned ?? 0);

const COLUMNS: ColumnDef<PoLine, any>[] = [
  {
    id: 'line',
    header: '#',
    meta: { align: 'right', numeric: true, cellClass: 'font-mono text-[11px] text-on-surface-variant w-8' },
    cell: ({ row }) => row.original.line_number,
  },
  {
    id: 'item',
    header: 'Item',
    meta: { cellClass: 'min-w-[10rem]' },
    cell: ({ row, table }) => {
      const l = row.original;
      const { highlighted } = table.options.meta as PaneMeta;
      return (
        <div className="min-w-0">
          {l.line_type === 'stock' && l.part_catalog_id ? (
            <ItemName size="sm" item={l} />
          ) : (
            <span className="text-on-surface">{l.notes || PO_LINE_TYPE_LABELS[l.line_type]}</span>
          )}
          {highlighted.has(l.id) && (
            <div className="text-[11px] font-medium text-primary">On this invoice</div>
          )}
          {l.status === 'cancelled' && (
            <div className="text-[11px] text-on-surface-variant">Cancelled</div>
          )}
        </div>
      );
    },
  },
  {
    id: 'ordered',
    header: 'Ordered',
    meta: { align: 'right', numeric: true, cellClass: 'font-mono whitespace-nowrap' },
    cell: ({ row }) => formatUnits(row.original.quantity_expected),
  },
  {
    id: 'received',
    header: 'Received',
    meta: { align: 'right', numeric: true, cellClass: 'font-mono whitespace-nowrap' },
    cell: ({ row }) => {
      const l = row.original;
      if (l.line_type !== 'stock') return <span className="text-on-surface-variant">—</span>;
      const returned = l.quantity_returned ?? 0;
      return (
        <>
          {formatUnits(netReceived(l))}
          {returned > 0 && (
            <div className="text-[11px] text-on-surface-variant">{formatUnits(returned)} returned</div>
          )}
        </>
      );
    },
  },
  {
    id: 'unit_cost',
    header: 'Unit cost',
    meta: { align: 'right', numeric: true, cellClass: 'font-mono whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => formatCents(row.original.unit_cost_cents),
  },
  {
    id: 'line_value',
    header: 'Line value',
    meta: { align: 'right', numeric: true, cellClass: 'font-mono whitespace-nowrap' },
    cell: ({ row }) => formatCents(row.original.line_total_cents),
  },
];

const HIGHLIGHT_ROW = 'bg-primary/10 shadow-[inset_3px_0_0_0_var(--color-primary,#ffcc33)]';

export default function PoReadOnlyPane({ purchaseOrderId, highlightLineIds = [] }: PoReadOnlyPaneProps) {
  const poApi = usePoApi();
  const [state, setState] = useState<PaneState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    poApi.get(purchaseOrderId)
      .then((po) => { if (!cancelled) setState({ status: 'ready', po }); })
      .catch(() => { if (!cancelled) setState({ status: 'error' }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrderId]);

  const highlighted = new Set(highlightLineIds);
  const po = state.status === 'ready' ? state.po : null;

  return (
    <section
      aria-label="The matched order"
      data-testid="po-readonly-pane"
      className="h-full flex flex-col bg-surface-container-low rounded-lg overflow-hidden"
    >
      <header className="shrink-0 px-4 pt-3 pb-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <h2 className="font-mono text-base font-semibold text-on-surface truncate">
            {po ? po.po_number : 'The order'}
          </h2>
          {po && (
            <Tag {...PO_STATUS_TAG[po.status]} className="shrink-0">{PO_STATUS_LABELS[po.status]}</Tag>
          )}
          <Link
            to={`/procurement/po/${purchaseOrderId}`}
            className="ml-auto shrink-0 rounded text-[13px] text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            Open the order
          </Link>
        </div>
        {po && (
          <dl className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-[13px]">
            {([
              ['Vendor', po.vendor?.name || 'Unknown'],
              ['Ordered on', formatEventDate(po.order_date) || '—'],
              ['Order value', formatCents(po.subtotal_cents)],
              ['Received', `${Math.round(po.percent_received)}%`],
            ] as [string, string][]).map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-[11px] text-on-surface-variant">{label}</dt>
                <dd className="truncate text-on-surface tabular-nums" title={value}>{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </header>

      {state.status === 'loading' && (
        <SkeletonGroup label="The order is loading." className="space-y-2 px-4 py-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
        </SkeletonGroup>
      )}
      {state.status === 'error' && (
        <Alert className="mx-4 mb-4">
          The order could not be loaded. Open it on its own page, or reload to try again.
        </Alert>
      )}
      {po && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <DataTable<PoLine>
            data={po.lines}
            columns={COLUMNS}
            getRowId={(l) => l.id}
            meta={{ highlighted } as PaneMeta}
            cellClass="px-2.5 py-2 text-[13px] text-on-surface align-top"
            rowProps={(l) => ({
              className: highlighted.has(l.id) ? HIGHLIGHT_ROW : '',
              'data-highlighted': highlighted.has(l.id) ? 'true' : undefined,
            })}
            empty={<EmptyState variant="area">This order has no lines.</EmptyState>}
          />
        </div>
      )}
    </section>
  );
}
